import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import AuthenticatedLayout from "../../Layouts/AuthenticatedLayout";
import {
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  IconButton,
  Button,
  FormHelperText,
  Alert,
  Snackbar,
  Backdrop,
  CircularProgress,
  Typography,
  InputAdornment,
} from "@mui/material";
import InfoSection from "../../Components/InfoSection";
import GroupedContainer from "../../Components/GroupedContainer";
import FormContainer from "../../Components/FormContainer";
import SecondaryButton from "../../Components/SecondaryButton";
import FileComponent from "../../Components/FileComponent";
import Swal from "sweetalert2";
import { IoMdAddCircle } from "react-icons/io";
import { RiSendPlaneFill } from "react-icons/ri";
import { GrPowerReset } from "react-icons/gr";
import Autocomplete from "@mui/material/Autocomplete";
import { useNavigate } from "react-router-dom";
import { CalendarToday } from "@mui/icons-material";

/* -------------------------------------------------------------------------- */
/* ------------------------------ ESTADOS INICIAIS -------------------------- */
/* -------------------------------------------------------------------------- */

const initialVehicle = {
  vin: "",
  carType: "",
  year: "",
  make: "",
  model: "",
  lotNumber: "",
  buyerNumber: "",
  isOperable: "Yes",
};

const initialFormData = {
  orderId: "",
  loadboard: "",
  paymentRate: "",
  paymentType: "",
  paymentMethod: "",
  brokerFee: "",
  storageFee: "",
  driver: "",
  // Pickup
  addresspickup: "",
  citypickup: "",
  Statepickup: "",
  zipcodepickup: "",
  PickupBusinessName: "",
  pickup_location_type: "",
  contactNamepickup: "",
  phonepickup: "",
  pickup_notes: "",
  emailpickup: "",
  expected_pickup_date: "",
  // Delivery
  addressdelivery: "",
  citydelivery: "",
  Statedelivery: "",
  zipcodedelivery: "",
  DeliveryBusinessName: "",
  delivery_location_type: "",
  contactNamedelivery: "",
  phonedelivery: "",
  emaildelivery: "",
  delivery_notes: "",
  expected_dropoff_date: "",
  // Shipper
  shipper: "",
  shipperSearch: "",
  // Load Items
  cars: [initialVehicle],
};

const initialNewShipperData = {
  shipperName: "",
  accountingEmail: "",
  shipperPhone: "",
  shipperType: "",
  infoEmail: "",
  address: "",
  city: "",
  state: "",
  zipcode: "",
};

function CreateLoad() {
  /* ---------------------------------------------------------------------- */
  /* ------------------------------ STATES --------------------------------- */
  /* ---------------------------------------------------------------------- */
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [driver, setDriver] = useState([]);
  const [pdfFile, setPdfFile] = useState(null);
  const [importedPdfData, setImportedPdfData] = useState("");
  const [carTypes, setCarTypes] = useState([]);
  const [shippers, setShippers] = useState([]);
  const [shipperSearch, setShipperSearch] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState(null);
  const shipperCreatedRef = useRef(false);
  const [shipperMessage, setShipperMessage] = useState("");
  const [shipperStatus, setShipperStatus] = useState(null);
  const [showAddShipperForm, setShowAddShipperForm] = useState(false);
  const [newShipperData, setNewShipperData] = useState(initialNewShipperData);

  // Novos estados para validação e feedback
  const [fieldErrors, setFieldErrors] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");
  const [submitLoading, setSubmitLoading] = useState(false);

  // Estado para controlar o FileComponent separadamente
  const [fileComponentKey, setFileComponentKey] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const navigate = useNavigate();

  /* ---------------------------------------------------------------------- */
  /* --------------------------- FUNÇÃO RESET ----------------------------- */
  /* ---------------------------------------------------------------------- */

  const resetForm = () => {
    setFormData(initialFormData);
    clearShipper();
    setShipperStatus(null);
    setShipperMessage("");
    setFieldErrors({});
    setImportedPdfData("");
    setShowAddShipperForm(false);
    setNewShipperData(initialNewShipperData);
  };

  const clearShipper = () => {
    setFormData((prev) => ({ ...prev, shipper: "" }));
    setShipperSearch("");
  };

  const clearAll = () => {
    resetForm();
    setPdfFile(null);
    setUploadedFiles([]);
    setFileComponentKey((prev) => prev + 1);
    showSnackbar("Form cleared successfully!", "success");
  };

  /* ---------------------------------------------------------------------- */
  /* --------------------------- VALIDAÇÃO -------------------------------- */
  /* ---------------------------------------------------------------------- */

  const validateEmail = (email) => {
    if (!email) return true; // Email é opcional
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    if (!phone) return true; // Telefone é opcional
    const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
    return phoneRegex.test(phone.replace(/\s/g, ""));
  };

  const validateZipcode = (zipcode) => {
    if (!zipcode) return false; // Zipcode é obrigatório
    const zipRegex = /^\d{5}(-\d{4})?$/;
    return zipRegex.test(zipcode);
  };

  const validateDate = (date) => {
    if (!date) return false; // Data é obrigatória
    const dateObj = new Date(date);
    return !isNaN(dateObj.getTime());
  };

  const validateField = (field, value) => {
    const errors = {};

    switch (field) {
      case "emailpickup":
      case "emaildelivery":
        if (value && !validateEmail(value)) {
          errors[field] = "Please enter a valid email address.";
        }
        break;

      case "phonepickup":
      case "phonedelivery":
        if (value && !validatePhone(value)) {
          errors[field] = "Please enter a valid phone number.";
        }
        break;

      case "zipcodepickup":
      case "zipcodedelivery":
        if (!validateZipcode(value)) {
          errors[field] = "Please enter a valid zipcode.";
        }
        break;

      case "expected_pickup_date":
      case "expected_dropoff_date":
        if (!validateDate(value)) {
          errors[field] = "Please select a valid date.";
        }
        break;

      case "paymentRate":
        if (value && (isNaN(value) || parseFloat(value) <= 0)) {
          errors[field] = "Payment rate must be a positive number.";
        }
        break;

      default:
        break;
    }

    return errors;
  };

  // Função para limpar erros de um campo específico
  const clearFieldError = (fieldName) => {
    setFieldErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  };

  // Handler genérico para campos com validação em tempo real
  const handleFieldChangeWithValidation = (field, value) => {
    handleFieldChange(field, value);

    // Limpa o erro do campo quando o usuário começa a digitar
    if (fieldErrors[field]) {
      clearFieldError(field);
    }

    // Validação em tempo real para campos específicos
    const validationErrors = validateField(field, value);
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors((prev) => ({ ...prev, ...validationErrors }));
    }
  };

  /* ---------------------------------------------------------------------- */
  /* --------------------------- VALIDAÇÃO SHIPPER ------------------------ */
  /* ---------------------------------------------------------------------- */

  const validateShipperFields = () => {
    const errors = {};

    // Campos obrigatórios para shipper
    const requiredShipperFields = [
      "shipperName",
      "accountingEmail",
      "shipperPhone",
      "shipperType",
      "address",
      "city",
      "state",
      "zipcode",
    ];

    requiredShipperFields.forEach((field) => {
      if (!newShipperData[field]) {
        errors[field] = "This field is required.";
      }
    });

    // Validações específicas
    if (
      newShipperData.accountingEmail &&
      !validateEmail(newShipperData.accountingEmail)
    ) {
      errors.accountingEmail = "Please enter a valid email address.";
    }

    if (newShipperData.infoEmail && !validateEmail(newShipperData.infoEmail)) {
      errors.infoEmail = "Please enter a valid email address.";
    }

    if (newShipperData.shipperPhone && !validatePhone(newShipperData.shipperPhone)) {
      errors.shipperPhone = "Please enter a valid phone number.";
    }

    if (newShipperData.zipcode && !validateZipcode(newShipperData.zipcode)) {
      errors.zipcode = "Please enter a valid zipcode.";
    }

    return errors;
  };

  /* ---------------------------------------------------------------------- */
  /* --------------------------- HANDLERS GERAIS --------------------------- */
  /* ---------------------------------------------------------------------- */

  const handleFieldChange = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const handleVehicleChange = (index, field, value) => {
    const updatedCars = formData.cars.map((car, i) =>
      i === index ? { ...car, [field]: value } : car
    );
    setFormData((prev) => ({ ...prev, cars: updatedCars }));

    // Limpa erros do campo quando o usuário começa a digitar
    if (
      fieldErrors[
        `car${index}${field.charAt(0).toUpperCase() + field.slice(1)}`
      ]
    ) {
      clearFieldError(
        `car${index}${field.charAt(0).toUpperCase() + field.slice(1)}`
      );
    }

    if (field === "vin" && value.length === 17) fetchVehicleData(index, value);
  };

  /* ----------------------- ADD / REMOVE VEHICLES ------------------------ */

  const addVehicle = () => {
    if (formData.cars.length < 9) {
      setFormData((prev) => ({
        ...prev,
        cars: [...prev.cars, { ...initialVehicle }],
      }));
    } else {
      showSnackbar("You can add up to 9 vehicles maximum.", "warning");
    }
  };

  const removeVehicle = (index) => {
    if (formData.cars.length > 1) {
      const updatedCars = formData.cars.filter((_, i) => i !== index);
      setFormData((prev) => ({ ...prev, cars: updatedCars }));
    }
  };

  /// Helper para converter "MM/DD/YYYY" → "YYYY-MM-DD"
  const toISO = (str) => {
    if (!str) return "";
    const parts = str.split("/");
    if (parts.length !== 3) return "";
    const [month, day, year] = parts;
    const isoStr = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    const dateObj = new Date(isoStr);
    return isNaN(dateObj) ? "" : dateObj.toISOString().split("T")[0];
  };


  const normalizeShipperText = (value = "") =>
    value
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^\w\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();

  const parseCityStateZip = (rawLine = "") => {
    const cleanLine = rawLine.toString().replace(/\s+/g, " ").trim();
    const match = cleanLine.match(/^(.+?),\s*([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/);

    if (!match) {
      return {
        city: "",
        state: "",
        zip_code: "",
      };
    }

    return {
      city: match[1].trim(),
      state: match[2].toUpperCase(),
      zip_code: match[3],
    };
  };

  const buildShipperStatePatch = (shipperRecord) => ({
    shipper: shipperRecord?.id || "",
  });

  const mergeShipperIntoList = (shipperRecord) => {
    if (!shipperRecord?.id) return;

    setShippers((prev) => {
      const exists = prev.some((item) => String(item.id) === String(shipperRecord.id));
      if (exists) {
        return prev.map((item) =>
          String(item.id) === String(shipperRecord.id) ? shipperRecord : item
        );
      }
      return [...prev, shipperRecord];
    });
  };

  const fetchAllShippersFresh = async () => {
    const userToken = localStorage.getItem("token");
    const { data } = await axios.get("https://new.cajuu.app/api/shipper", {
      headers: {
        "Screen-Name": "/NewLoad",
        Authorization: `Bearer ${userToken}`,
      },
    });

    const freshShippers = data?.data || [];
    setShippers(freshShippers);
    return freshShippers;
  };

  const findBestShipperMatch = (shipperList = [], rawName = "") => {
    const normalizedTarget = normalizeShipperText(rawName);

    if (!normalizedTarget) return null;

    const exactMatch = shipperList.find(
      (shipperOption) =>
        normalizeShipperText(shipperOption?.shipper_name || "") === normalizedTarget
    );

    if (exactMatch) return exactMatch;

    const startsWithMatch = shipperList.find((shipperOption) =>
      normalizeShipperText(shipperOption?.shipper_name || "").startsWith(normalizedTarget)
    );

    if (startsWithMatch) return startsWithMatch;

    return (
      shipperList.find((shipperOption) =>
        normalizeShipperText(shipperOption?.shipper_name || "").includes(normalizedTarget)
      ) || null
    );
  };

  const buildImportedShipperPayload = (importedShipper = {}) => {
    const addressLine1 = (importedShipper?.address_line_1 || "").trim();
    const addressLine2 = (importedShipper?.address_line_2 || "").trim();
    const parsedFromLine2 = parseCityStateZip(addressLine2);

    const city = (importedShipper?.city || parsedFromLine2.city || "").trim();
    const state = (importedShipper?.state || parsedFromLine2.state || "").trim();
    const zipCode = (importedShipper?.zip_code || parsedFromLine2.zip_code || "").trim();
    const address = (importedShipper?.address || addressLine1 || "").trim();

    return {
      shipper_name: (importedShipper?.name || "").trim(),
      type: (importedShipper?.type || "Broker").trim(),
      phone: (importedShipper?.phone || "").trim(),
      accounting_email: (importedShipper?.accounting_email || importedShipper?.email || "").trim(),
      info_email: (importedShipper?.info_email || importedShipper?.email || "").trim(),
      address,
      city,
      state,
      zip_code: zipCode,
      address_line_1: addressLine1 || null,
      address_line_2: addressLine2 || null,
    };
  };

  const resolveShipper = async (
    importedShipper,
    { allowCreate = true, forceRefresh = true } = {}
  ) => {
    const shipperName = importedShipper?.name?.trim();

    if (!shipperName) {
      return {
        shipperId: "",
        shipperName: "",
        shipperRecord: null,
      };
    }

    let shipperList = shippers;

    if (!shipperList.length || forceRefresh) {
      try {
        shipperList = await fetchAllShippersFresh();
      } catch (error) {
        console.error("Error refreshing shippers before resolving shipper:", error);
      }
    }

    let matchedShipper = findBestShipperMatch(shipperList, shipperName);

    if (matchedShipper) {
      mergeShipperIntoList(matchedShipper);

      return {
        shipperId: matchedShipper.id,
        shipperName: matchedShipper.shipper_name,
        shipperRecord: matchedShipper,
      };
    }

    if (!allowCreate) {
      return {
        shipperId: "",
        shipperName,
        shipperRecord: null,
      };
    }

    try {
      const shipperPayload = buildImportedShipperPayload(importedShipper);
      const userToken = localStorage.getItem("token");

      const shipperResponse = await axios.post(
        "https://new.cajuu.app/api/shipper",
        shipperPayload,
        {
          headers: {
            "Screen-Name": "/NewLoad",
            Authorization: `Bearer ${userToken}`,
          },
        }
      );

      const createdShipper = shipperResponse?.data?.data || null;

      if (createdShipper?.id) {
        mergeShipperIntoList(createdShipper);

        return {
          shipperId: createdShipper.id,
          shipperName: createdShipper.shipper_name,
          shipperRecord: createdShipper,
        };
      }
    } catch (shipperError) {
      console.error("Error creating shipper automatically:", shipperError);
    }

    try {
      const freshShippers = await fetchAllShippersFresh();
      matchedShipper = findBestShipperMatch(freshShippers, shipperName);

      if (matchedShipper) {
        mergeShipperIntoList(matchedShipper);

        return {
          shipperId: matchedShipper.id,
          shipperName: matchedShipper.shipper_name,
          shipperRecord: matchedShipper,
        };
      }
    } catch (refreshError) {
      console.error("Error reloading shippers after automatic creation attempt:", refreshError);
    }

    return {
      shipperId: "",
      shipperName,
      shipperRecord: null,
    };
  };

  const ensureShipperResolvedBeforeSubmit = async (currentFormData) => {
    if (currentFormData.shipper || !shipperSearch.trim()) {
      return currentFormData;
    }

    const resolved = await resolveShipper(
      { name: shipperSearch.trim() },
      { allowCreate: false, forceRefresh: true }
    );

    if (!resolved.shipperId) {
      return currentFormData;
    }

    const nextFormData = {
      ...currentFormData,
      shipper: resolved.shipperId,
    };

    setFormData((prev) => ({
      ...prev,
      ...buildShipperStatePatch(resolved.shipperRecord),
    }));
    setShipperSearch(resolved.shipperName);

    return nextFormData;
  };


  const handleImportPdf = async (fileParam) => {
    const fileToUpload = fileParam || pdfFile;
    if (!fileToUpload) {
      showSnackbar("Please select a PDF file before importing.", "warning");
      return;
    }

    setImportLoading(true);

    try {
      const formDataPdf = new FormData();
      formDataPdf.append("pdf", fileToUpload.file);

      const { data: apiData } = await axios.post(
        "https://new.cajuu.app/api/import_pdf",
        formDataPdf,
        {
          headers: {
            "Screen-Name": "/NewLoad",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const extracted = apiData.data || apiData;

      setImportedPdfData(JSON.stringify(extracted, null, 2));

      const vehiclesArray = extracted.vehicles || [];
      const mappedCars = vehiclesArray.map((v) => ({
        vin: v.vin || "",
        make: v.make || "",
        model: v.model || "",
        year: v.year || "",
        carType: v.type || "",
        lotNumber: v.lot_number || "",
        buyerNumber: v.buyer_number || "",
        isOperable: v.is_operable ? "Yes" : "No",
      }));

      let resolvedShipper = {
        shipperId: "",
        shipperName: "",
        shipperRecord: null,
      };

      if (extracted.shipper?.name) {
        resolvedShipper = await resolveShipper(extracted.shipper, {
          allowCreate: true,
          forceRefresh: true,
        });

        if (resolvedShipper.shipperId) {
          showSnackbar(
            `Shipper "${resolvedShipper.shipperName}" linked successfully.`,
            "success"
          );
        } else {
          setShipperSearch(extracted.shipper.name.trim());
          showSnackbar(
            "The shipper name was extracted, but it could not be linked automatically. Please confirm it before saving.",
            "warning"
          );
        }
      } else {
        clearShipper();
      }

      setFormData(() => ({
        ...initialFormData,
        loadboard: extracted.loadboard || "",
        orderId: extracted.load_id || "",
        paymentRate: extracted.total_price || "",
        paymentType: extracted.payment_type || "",
        brokerFee: extracted.broker_fee || "",
        storageFee: extracted.storage_fee || "",
        shipper: resolvedShipper.shipperId || "",
        addresspickup: extracted.origin?.address || "",
        citypickup: extracted.origin?.city || "",
        Statepickup: extracted.origin?.state || "",
        zipcodepickup: extracted.origin?.zip_code || "",
        contactNamepickup: extracted.origin?.company || "",
        expected_pickup_date: toISO(extracted.scheduled_pickup_estimated),
        addressdelivery: extracted.destination?.address || "",
        citydelivery: extracted.destination?.city || "",
        Statedelivery: extracted.destination?.state || "",
        zipcodedelivery: extracted.destination?.zip_code || "",
        contactNamedelivery: extracted.destination?.location || "",
        expected_dropoff_date: toISO(extracted.scheduled_delivery_estimated),
        cars: mappedCars.length > 0 ? mappedCars : [initialVehicle],
      }));

      if (resolvedShipper.shipperName) {
        setShipperSearch(resolvedShipper.shipperName);
      }

      showSnackbar("PDF imported successfully!", "success");
    } catch (err) {
      console.error("Error importing PDF:", err);
      showSnackbar("Failed to import PDF.", "error");
    } finally {
      setImportLoading(false);
      setLoading(false);
    }
  };

  /* ----------------------------- API CALLS ------------------------------ */


  const fetchVehicleData = async (index, vin) => {
    try {
      setLoading(true);
      const userToken = localStorage.getItem("token");
      const { data } = await axios.get(`https://new.cajuu.app/api/vin/${vin}`, {
        headers: {
          "Screen-Name": "/NewLoad",
          Authorization: `Bearer ${userToken}`,
        },
      });
      const { year, make, model } = data;
      const updatedCars = formData.cars.map((car, i) =>
        i === index ? { ...car, year, make, model } : car
      );
      setFormData((prev) => ({ ...prev, cars: updatedCars }));
    } catch (err) {
      console.error("Erro VIN:", err);
      showSnackbar("Failed to fetch vehicle data.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleZipcodeChange = async (field, value) => {
    handleFieldChangeWithValidation(field, value);
    if (/^\d{5}$/.test(value)) {
      try {
        setLoading(true);
        const userToken = localStorage.getItem("token");
        const { data } = await axios.get(`https://api.zippopotam.us/us/${value}`);
        const place = data.places[0];
        if (field === "zipcodepickup") {
          setFormData((prev) => ({
            ...prev,
            citypickup: place["place name"] || "",
            Statepickup: place["state abbreviation"] || "",
          }));
        } else {
          setFormData((prev) => ({
            ...prev,
            citydelivery: place["place name"] || "",
            Statedelivery: place["state abbreviation"] || "",
          }));
        }
      } catch (err) {
        console.error("Erro CEP:", err);
        showSnackbar("Unable to get address from zipcode.", "error");
      } finally {
        setLoading(false);
      }
    }
  };

  const fetchDriver = useCallback(async () => {
    try {
      setLoading(true);
      const userToken = localStorage.getItem("token");
      const { data } = await axios.get("https://new.cajuu.app/api/driver", {
        headers: {
          "Screen-Name": "/NewLoad",
          Authorization: `Bearer ${userToken}`,
        },
      });
      setDriver(data.data);
    } catch (err) {
      console.error("Erro drivers:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCarTypes = useCallback(async () => {
    try {
      setLoading(true);
      const userToken = localStorage.getItem("token");
      const { data } = await axios.get("https://new.cajuu.app/api/cartype", {
        headers: {
          "Screen-Name": "/NewLoad",
          Authorization: `Bearer ${userToken}`,
        },
      });
      setCarTypes(data.data);
    } catch (err) {
      console.error("Erro car types:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchShippers = useCallback(async () => {
    try {
      setLoading(true);
      const userToken = localStorage.getItem("token");
      const { data } = await axios.get("https://new.cajuu.app/api/shipper", {
        headers: {
          "Screen-Name": "/NewLoad",
          Authorization: `Bearer ${userToken}`,
        },
      });
      setShippers(data.data);
    } catch (err) {
      console.error("Erro shippers:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDriver();
    fetchCarTypes();
    fetchShippers();
  }, [fetchDriver, fetchCarTypes, fetchShippers]);

  /* --------------------------- SNACKBAR FUNCTIONS ----------------------- */

  const showSnackbar = (message, severity = "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  /* --------------------------- SUBMIT FORM ------------------------------ */


  const validateRequiredFields = (currentFormData = formData) => {
    const required = [
      "orderId",
      "addresspickup",
      "citypickup",
      "Statepickup",
      "zipcodepickup",
      "addressdelivery",
      "citydelivery",
      "Statedelivery",
      "zipcodedelivery",
      "paymentRate",
      "paymentType",
      "paymentMethod",
      "expected_pickup_date",
      "expected_dropoff_date",
      "shipper",
    ];
    const errors = {};

    required.forEach((field) => {
      if (!currentFormData[field]) {
        errors[field] = "This field is required.";
      }
    });

    currentFormData.cars.forEach((car, i) => {
      if (!car.year) {
        errors[`car${i}Year`] = "Year is required.";
      } else if (car.year.toString().length > 4) {
        errors[`car${i}Year`] = "Year must be 4 digits or less.";
      }

      if (!car.make) {
        errors[`car${i}Make`] = "Make is required.";
      } else if (car.make.length > 255) {
        errors[`car${i}Make`] = "Make must be 255 characters or less.";
      }

      if (!car.model) {
        errors[`car${i}Model`] = "Model is required.";
      } else if (car.model.length > 255) {
        errors[`car${i}Model`] = "Model must be 255 characters or less.";
      }

      if (!car.carType) {
        errors[`car${i}CarType`] = "Car type is required.";
      }

      if (car.isOperable !== "Yes" && car.isOperable !== "No") {
        errors[`car${i}IsOperable`] = "Please select if vehicle is operable.";
      }
    });

    if (currentFormData.emailpickup && !validateEmail(currentFormData.emailpickup)) {
      errors.emailpickup = "Please enter a valid email address.";
    }

    if (currentFormData.emaildelivery && !validateEmail(currentFormData.emaildelivery)) {
      errors.emaildelivery = "Please enter a valid email address.";
    }

    if (currentFormData.phonepickup && !validatePhone(currentFormData.phonepickup)) {
      errors.phonepickup = "Please enter a valid phone number.";
    }

    if (currentFormData.phonedelivery && !validatePhone(currentFormData.phonedelivery)) {
      errors.phonedelivery = "Please enter a valid phone number.";
    }

    if (!validateZipcode(currentFormData.zipcodepickup)) {
      errors.zipcodepickup = "Please enter a valid zipcode.";
    }

    if (!validateZipcode(currentFormData.zipcodedelivery)) {
      errors.zipcodedelivery = "Please enter a valid zipcode.";
    }

    if (!validateDate(currentFormData.expected_pickup_date)) {
      errors.expected_pickup_date = "Please select a valid pickup date.";
    }

    if (!validateDate(currentFormData.expected_dropoff_date)) {
      errors.expected_dropoff_date = "Please select a valid dropoff date.";
    }

    if (
      currentFormData.paymentRate &&
      (isNaN(currentFormData.paymentRate) || parseFloat(currentFormData.paymentRate) <= 0)
    ) {
      errors.paymentRate = "Payment rate must be a positive number.";
    }

    return errors;
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    setFieldErrors({});

    let submitFormData = { ...formData };
    submitFormData = await ensureShipperResolvedBeforeSubmit(submitFormData);

    const validationErrors = validateRequiredFields(submitFormData);
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      showSnackbar("Please fill in all required fields correctly.", "error");
      return;
    }

    setSubmitLoading(true);

    try {
      const userToken = localStorage.getItem("token");

      const payload = {
        driver_id: submitFormData.driver ? parseInt(submitFormData.driver) : null,
        shipper_id: submitFormData.shipper || null,
        loadboard: submitFormData.loadboard,
        order_id: submitFormData.orderId,
        pickup_address: submitFormData.addresspickup,
        pickup_city: submitFormData.citypickup,
        pickup_state: submitFormData.Statepickup,
        pickup_zipcode: submitFormData.zipcodepickup,
        pickup_customer_name: submitFormData.contactNamepickup,
        pickup_customer_phone: submitFormData.phonepickup,
        pickup_customer_email: submitFormData.emailpickup,
        pickup_notes: submitFormData.pickup_notes,
        pickup_business_name: submitFormData.PickupBusinessName,
        pickup_location_type: submitFormData.pickup_location_type,
        delivery_address: submitFormData.addressdelivery,
        delivery_city: submitFormData.citydelivery,
        delivery_state: submitFormData.Statedelivery,
        delivery_zipcode: submitFormData.zipcodedelivery,
        delivery_customer_name: submitFormData.contactNamedelivery,
        delivery_customer_phone: submitFormData.phonedelivery,
        delivery_customer_email: submitFormData.emaildelivery,
        delivery_notes: submitFormData.delivery_notes,
        delivery_business_name: submitFormData.DeliveryBusinessName,
        delivery_location_type: submitFormData.delivery_location_type,
        payment_rate: parseFloat(submitFormData.paymentRate),
        payment_method: submitFormData.paymentMethod,
        payment_type: submitFormData.paymentType,
        broker_fee: submitFormData.brokerFee,
        storage_fee: submitFormData.storageFee,
        dispatch_date: new Date().toISOString().split("T")[0],
        expected_pickup_date: submitFormData.expected_pickup_date,
        expected_dropoff_date: submitFormData.expected_dropoff_date,
        load_items: submitFormData.cars.map((v) => ({
          car_type_id: v.carType ? parseInt(v.carType) : null,
          model: v.model,
          year: v.year,
          vin: v.vin,
          make: v.make,
          buyer_number: v.buyerNumber,
          lot_number: v.lotNumber,
          is_operable: v.isOperable === "Yes",
        })),
      };

      await axios.post("https://new.cajuu.app/api/loadtransport", payload, {
        headers: {
          "Screen-Name": "/Load",
          Authorization: `Bearer ${userToken}`,
        },
      });

      showSnackbar("Load created successfully!", "success");

      setTimeout(() => {
        navigate("/Load");
      }, 1500);
    } catch (err) {
      console.error("Error creating load:", err);
      const errorMessage =
        err.response?.data?.message || "Failed to create load. Please try again.";
      showSnackbar(errorMessage, "error");
    } finally {
      setSubmitLoading(false);
    }
  };

  /* --------------------------- HANDLE ADD SHIPPER ----------------------- */


  const handleAddShipper = async (e) => {
    e.preventDefault();

    // Validar campos do shipper
    const shipperErrors = validateShipperFields();
    if (Object.keys(shipperErrors).length > 0) {
      // Mostrar erros de validação
      Object.keys(shipperErrors).forEach((field) => {
        showSnackbar(shipperErrors[field], "error");
      });
      return;
    }

    let loading;
    try {
      loading = Swal.fire({
        title: "Loading...",
        text: "Please wait.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const userToken = localStorage.getItem("token");
      const minimumLoadingTime = 2000;
      const loadingStartTime = Date.now();

      const response = await axios.post(
        "https://new.cajuu.app/api/shipper",
        {
          shipper_name: newShipperData.shipperName,
          accounting_email: newShipperData.accountingEmail,
          phone: newShipperData.shipperPhone,
          type: newShipperData.shipperType,
          info_email: newShipperData.infoEmail,
          address: newShipperData.address,
          city: newShipperData.city,
          state: newShipperData.state,
          zip_code: newShipperData.zipcode,
        },
        {
          headers: {
            "Screen-Name": "/NewLoad",
            Authorization: `Bearer ${userToken}`,
          },
        }
      );

      const elapsedLoadingTime = Date.now() - loadingStartTime;
      const remainingLoadingTime = minimumLoadingTime - elapsedLoadingTime;

      if (remainingLoadingTime > 0) {
        await new Promise((resolve) => setTimeout(resolve, remainingLoadingTime));
      }

      Swal.close();

      if (response.status === 200 || response.status === 201) {
        setShippers((prev) => [...prev, response.data.data]);
        setShowAddShipperForm(false);
        setShipperSearch("");
        setNewShipperData(initialNewShipperData); // Reset para estado inicial

        // Definir o shipper recém-criado no formulário principal
        setFormData((prev) => ({ ...prev, shipper: response.data.data.id }));
        setShipperSearch(response.data.data.shipper_name);

        showSnackbar("Shipper added successfully.", "success");
      } else {
        showSnackbar("Could not add the shipper. Please try again.", "error");
      }
    } catch (error) {
      if (loading) Swal.close();
      console.error("Error adding shipper:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Could not add the shipper. Please try again.";
      showSnackbar(errorMessage, "error");
    } finally {
      if (loading) Swal.close();
    }
  };

  /* ----------------------------- RENDER ---------------------------------- */

  return (
    <AuthenticatedLayout>
      <div className="text-white ml-5 text-2xl">
        <InfoSection title="LOAD" description="Create load" />

        <Backdrop
          open={importLoading}
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress color="inherit" />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Processing PDF, please wait...
          </Typography>
        </Backdrop>

        <GroupedContainer>
          <FileComponent
            key={fileComponentKey}
            title="Drop dispatch sheet here"
            subtitle="Click to upload or drag and drop your document here (Max 4 MB)"
            acceptedFileTypes="pdf"
            maxFiles={1}
            files={uploadedFiles}
            onChange={(files) => {
              if (files && files[0]) {
                setPdfFile(files[0]);
                setUploadedFiles(files);
                handleImportPdf(files[0]);
              } else {
                // Quando der clear - apenas limpar o formulário, mas manter o PDF
                resetForm();
                showSnackbar("Form cleared successfully!", "success");
              }
            }}
            onRemove={() => {
              setPdfFile(null);
              setUploadedFiles([]);
              resetForm();
            }}
          />
        </GroupedContainer>
          { /*}
        {importedPdfData && (
          <TextField
            label="Extracted Data"
            value={importedPdfData}
            multiline
            rows={4}
            fullWidth
            disabled
            color="secondary"
            sx={{ marginTop: "1rem" }}
          />
        )}
          {*/}

        {/* ---------------- General Info ---------------- */}
        <GroupedContainer className="mt-[1rem]">
          <InfoSection title="General Info" />
          <FormContainer>
            <TextField
              label="Order Id"
              value={formData.orderId}
              onChange={(e) => handleFieldChangeWithValidation("orderId", e.target.value)}
              required
              color="secondary"
              error={!!fieldErrors.orderId}
              helperText={fieldErrors.orderId}
              sx={{
                "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "red !important",
                },
                "& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "currentColor",
                },
              }}
            />

            {/* Loadboard */}
            <FormControl fullWidth>
              <InputLabel id="loadboard-label">Loadboard</InputLabel>
              <Select
                labelId="loadboard-label"
                value={formData.loadboard}
                onChange={(e) => handleFieldChange("loadboard", e.target.value)}
                label="Loadboard"
                color="secondary"
              >
                <MenuItem value="Cajuu">Cajuu</MenuItem>
                <MenuItem value="Central dispatch">Central dispatch</MenuItem>
                <MenuItem value="Ship.cars">Ship.cars</MenuItem>
                <MenuItem value="Autosled">Autosled</MenuItem>
                <MenuItem value="Acertus">Acertus</MenuItem>
                <MenuItem value="Runbugy">Runbugy</MenuItem>
                <MenuItem value="Ready">Ready</MenuItem>
                <MenuItem value="Carsarrive">Carsarrive</MenuItem>
                <MenuItem value="United">United</MenuItem>
                <MenuItem value="ACV Transportation">ACV Transportation</MenuItem>
                <MenuItem value="Uship">Uship</MenuItem>
                <MenuItem value="Email">Email</MenuItem>
              </Select>
            </FormControl>

            {/* Driver */}
            <FormControl fullWidth>
              <InputLabel id="driver-label">Driver</InputLabel>
              <Select
                labelId="driver-label"
                value={formData.driver}
                onChange={(e) => handleFieldChange("driver", e.target.value)}
                label="Driver"
                color="secondary"
              >
                <MenuItem value="">No driver assigned</MenuItem>
                {driver.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.user ? `${d.user.first_name} ${d.user.last_name}` : "Unknown Driver"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Shipper Autocomplete - CORRIGIDO */}
            <FormControl fullWidth variant="outlined" error={!!fieldErrors.shipper}>
              <Autocomplete
                freeSolo
                options={shippers}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : option.shipper_name
                }
                value={
                  shippers.find((option) => String(option.id) === String(formData.shipper)) ||
                  null
                }
                inputValue={shipperSearch}
                onInputChange={(_, value, reason) => {
                  setShipperSearch(value);

                  // Limpa o erro do shipper quando o usuário começa a digitar
                  if (reason === "input" && fieldErrors.shipper) {
                    clearFieldError("shipper");
                  }

                  // Se o usuário está digitando, limpa o shipper selecionado
                  if (reason === "input") {
                    setFormData((prev) => ({ ...prev, shipper: "" }));
                  }
                }}
                onChange={(_, selected) => {
                  if (selected && typeof selected !== "string") {
                    setFormData((prev) => ({ ...prev, shipper: selected.id }));
                    setShipperSearch(selected.shipper_name);
                    setShipperStatus("exists");
                    setShipperMessage("Shipper already exists in the system.");
                    // Limpa erro do shipper quando um é selecionado
                    if (fieldErrors.shipper) {
                      clearFieldError("shipper");
                    }
                  } else if (selected === null) {
                    setFormData((prev) => ({ ...prev, shipper: "" }));
                    setShipperSearch("");
                  } else if (typeof selected === "string") {
                    // Quando o usuário digita um texto livre
                    setShipperSearch(selected);
                    setFormData((prev) => ({ ...prev, shipper: "" }));
                  }
                }}
                filterOptions={(options, state) => {
                  // Filtra os shippers baseado no texto digitado
                  const inputValue = normalizeShipperText(state.inputValue);
                  if (!inputValue) return options;

                  return options.filter((option) =>
                    normalizeShipperText(option.shipper_name).includes(inputValue)
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Shipper *"
                    variant="outlined"
                    color="secondary"
                    error={!!fieldErrors.shipper}
                    helperText={fieldErrors.shipper}
                    onBlur={async () => {
                      if (!formData.shipper && shipperSearch.trim()) {
                        const resolved = await resolveShipper(
                          { name: shipperSearch.trim() },
                          { allowCreate: false, forceRefresh: true }
                        );

                        if (resolved.shipperId) {
                          setFormData((prev) => ({
                            ...prev,
                            ...buildShipperStatePatch(resolved.shipperRecord),
                          }));
                          setShipperSearch(resolved.shipperName);

                          if (fieldErrors.shipper) {
                            clearFieldError("shipper");
                          }
                        }
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.endAdornment}
                          <IconButton
                            size="small"
                            color={fieldErrors.shipper ? "error" : "secondary"}
                            onClick={() => setShowAddShipperForm((prev) => !prev)}
                            sx={{ ml: 0.5 }}
                          >
                            <IoMdAddCircle size={20} />
                          </IconButton>
                        </>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                        borderColor: "red !important",
                      },
                      "& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
                        borderColor: "currentColor",
                      },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: fieldErrors.shipper ? "red" : "#fff",
                        },
                        "&:hover fieldset": {
                          borderColor: fieldErrors.shipper ? "red" : "#fff",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: fieldErrors.shipper ? "red" : "#fff",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: fieldErrors.shipper ? "red" : "#fff",
                        "&.Mui-focused": {
                          color: fieldErrors.shipper ? "red" : "#fff",
                        },
                      },
                      "& .MuiFormHelperText-root": {
                        color: "red",
                      },
                    }}
                  />
                )}
                sx={{
                  "& .MuiAutocomplete-popupIndicator": {
                    color: fieldErrors.shipper ? "red" : "#fff",
                  },
                  "& .MuiAutocomplete-clearIndicator": {
                    color: fieldErrors.shipper ? "red" : "#fff",
                  },
                }}
              />
            </FormControl>

            {showAddShipperForm && (
              <div className="mt-4 p-4 border rounded" style={{ backgroundColor: "transparent" }}>
                <h3 className="text-lg font-semibold mb-2 text-white">Add New Shipper</h3>

                <FormContainer grid={2}>
                  {/* Nome do Shipper - Obrigatório */}
                  <TextField
                    label="Shipper Name"
                    variant="outlined"
                    fullWidth
                    margin="dense"
                    size="small"
                    required
                    value={newShipperData.shipperName}
                    onChange={(e) =>
                      setNewShipperData((prev) => ({
                        ...prev,
                        shipperName: e.target.value,
                      }))
                    }
                    InputLabelProps={{ style: { color: "#fff" } }}
                    InputProps={{ style: { color: "#fff" } }}
                    sx={{
                      backgroundColor: "transparent",
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#fff" },
                        "&:hover fieldset": { borderColor: "#fff" },
                        "&.Mui-focused fieldset": { borderColor: "#fff" },
                      },
                    }}
                  />

                  {/* Email de Contabilidade - Obrigatório */}
                  <TextField
                    label="Accounting Email"
                    variant="outlined"
                    fullWidth
                    margin="dense"
                    size="small"
                    required
                    value={newShipperData.accountingEmail}
                    onChange={(e) =>
                      setNewShipperData((prev) => ({
                        ...prev,
                        accountingEmail: e.target.value,
                      }))
                    }
                    InputLabelProps={{ style: { color: "#fff" } }}
                    InputProps={{ style: { color: "#fff" } }}
                    sx={{
                      backgroundColor: "transparent",
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#fff" },
                        "&:hover fieldset": { borderColor: "#fff" },
                        "&.Mui-focused fieldset": { borderColor: "#fff" },
                      },
                    }}
                  />

                  {/* Telefone - Obrigatório */}
                  <TextField
                    label="Phone"
                    variant="outlined"
                    fullWidth
                    margin="dense"
                    size="small"
                    required
                    value={newShipperData.shipperPhone}
                    onChange={(e) =>
                      setNewShipperData((prev) => ({
                        ...prev,
                        shipperPhone: e.target.value,
                      }))
                    }
                    InputLabelProps={{ style: { color: "#fff" } }}
                    InputProps={{ style: { color: "#fff" } }}
                    sx={{
                      backgroundColor: "transparent",
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#fff" },
                        "&:hover fieldset": { borderColor: "#fff" },
                        "&.Mui-focused fieldset": { borderColor: "#fff" },
                      },
                    }}
                  />

                  {/* Tipo de Shipper - Obrigatório */}
                  <FormControl fullWidth margin="dense" size="small">
                    <InputLabel style={{ color: "#fff" }}>Shipper Type *</InputLabel>
                    <Select
                      value={newShipperData.shipperType}
                      onChange={(e) =>
                        setNewShipperData((prev) => ({
                          ...prev,
                          shipperType: e.target.value,
                        }))
                      }
                      label="Shipper Type"
                      style={{ color: "#fff" }}
                      sx={{
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#fff",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#fff",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#fff",
                        },
                      }}
                    >
                      <MenuItem value="Broker">Broker</MenuItem>
                      <MenuItem value="Dealership">Dealership</MenuItem>
                      <MenuItem value="Auction">Auction</MenuItem>
                      <MenuItem value="Individual">Individual</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Email de Informações - Opcional */}
                  <TextField
                    label="Info Email"
                    variant="outlined"
                    fullWidth
                    margin="dense"
                    size="small"
                    value={newShipperData.infoEmail}
                    onChange={(e) =>
                      setNewShipperData((prev) => ({
                        ...prev,
                        infoEmail: e.target.value,
                      }))
                    }
                    InputLabelProps={{ style: { color: "#fff" } }}
                    InputProps={{ style: { color: "#fff" } }}
                    sx={{
                      backgroundColor: "transparent",
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#fff" },
                        "&:hover fieldset": { borderColor: "#fff" },
                        "&.Mui-focused fieldset": { borderColor: "#fff" },
                      },
                    }}
                  />

                  {/* Endereço - Obrigatório */}
                  <TextField
                    label="Address"
                    variant="outlined"
                    fullWidth
                    margin="dense"
                    size="small"
                    required
                    value={newShipperData.address}
                    onChange={(e) =>
                      setNewShipperData((prev) => ({
                        ...prev,
                        address: e.target.value,
                      }))
                    }
                    InputLabelProps={{ style: { color: "#fff" } }}
                    InputProps={{ style: { color: "#fff" } }}
                    sx={{
                      backgroundColor: "transparent",
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#fff" },
                        "&:hover fieldset": { borderColor: "#fff" },
                        "&.Mui-focused fieldset": { borderColor: "#fff" },
                      },
                    }}
                  />

                  {/* Cidade - Obrigatório */}
                  <TextField
                    label="City"
                    variant="outlined"
                    fullWidth
                    margin="dense"
                    size="small"
                    required
                    value={newShipperData.city}
                    onChange={(e) =>
                      setNewShipperData((prev) => ({
                        ...prev,
                        city: e.target.value,
                      }))
                    }
                    InputLabelProps={{ style: { color: "#fff" } }}
                    InputProps={{ style: { color: "#fff" } }}
                    sx={{
                      backgroundColor: "transparent",
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#fff" },
                        "&:hover fieldset": { borderColor: "#fff" },
                        "&.Mui-focused fieldset": { borderColor: "#fff" },
                      },
                    }}
                  />

                  {/* Estado - Obrigatório */}
                  <TextField
                    label="State"
                    variant="outlined"
                    fullWidth
                    margin="dense"
                    size="small"
                    required
                    value={newShipperData.state}
                    onChange={(e) =>
                      setNewShipperData((prev) => ({
                        ...prev,
                        state: e.target.value,
                      }))
                    }
                    InputLabelProps={{ style: { color: "#fff" } }}
                    InputProps={{ style: { color: "#fff" } }}
                    sx={{
                      backgroundColor: "transparent",
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#fff" },
                        "&:hover fieldset": { borderColor: "#fff" },
                        "&.Mui-focused fieldset": { borderColor: "#fff" },
                      },
                    }}
                  />

                  {/* CEP - Obrigatório */}
                  <TextField
                    label="Zipcode"
                    variant="outlined"
                    fullWidth
                    margin="dense"
                    size="small"
                    required
                    value={newShipperData.zipcode}
                    onChange={(e) =>
                      setNewShipperData((prev) => ({
                        ...prev,
                        zipcode: e.target.value,
                      }))
                    }
                    InputLabelProps={{ style: { color: "#fff" } }}
                    InputProps={{ style: { color: "#fff" } }}
                    sx={{
                      backgroundColor: "transparent",
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#fff" },
                        "&:hover fieldset": { borderColor: "#fff" },
                        "&.Mui-focused fieldset": { borderColor: "#fff" },
                      },
                    }}
                  />
                </FormContainer>

                <div className="flex gap-2 mt-4">
                  <Button variant="contained" color="warning" onClick={handleAddShipper}>
                    Add Shipper
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      setShowAddShipperForm(false);
                      setNewShipperData(initialNewShipperData);
                    }}
                    sx={{ color: "#fff", borderColor: "#fff" }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </FormContainer>
        </GroupedContainer>

        {/* ---------------- Payment Info ---------------- */}
        <GroupedContainer className="mt-[5rem]">
          <InfoSection title="Payment Info" />
          <FormContainer>
            <TextField
              label="Payment Rate"
              type="number"
              value={formData.paymentRate}
              onChange={(e) =>
                handleFieldChangeWithValidation("paymentRate", e.target.value)
              }
              required
              color="secondary"
              error={!!fieldErrors.paymentRate}
              helperText={fieldErrors.paymentRate}
              sx={{
                "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "red !important",
                },
                "& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "currentColor",
                },
              }}
            />

            {/* Payment Type - CORRIGIDO (asterisco automático do MUI) */}
            <FormControl fullWidth error={!!fieldErrors.paymentType} required>
              <InputLabel
                id="payment-type-label"
                sx={{
                  color: fieldErrors.paymentType ? "red" : "#fff",
                  "&.Mui-focused": {
                    color: fieldErrors.paymentType ? "red" : "#fff",
                  },
                  // ✅ força o asterisco automático a ficar vermelho
                  "& .MuiFormLabel-asterisk": { color: "red" },
                  "& .MuiInputLabel-asterisk": { color: "red" },
                }}
              >
                Payment Type
              </InputLabel>
              <Select
                labelId="payment-type-label"
                value={formData.paymentType}
                onChange={(e) => {
                  handleFieldChange("paymentType", e.target.value);
                  // Limpar o erro quando o usuário selecionar algo
                  if (fieldErrors.paymentType) {
                    clearFieldError("paymentType");
                  }
                }}
                label="Payment Type"
                color="secondary"
                sx={{
                  "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                    borderColor: "red !important",
                  },
                  "& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
                    borderColor: "currentColor",
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: fieldErrors.paymentType ? "red" : "#fff",
                    },
                    "&:hover fieldset": {
                      borderColor: fieldErrors.paymentType ? "red" : "#fff",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: fieldErrors.paymentType ? "red" : "#fff",
                    },
                  },
                  "& .MuiSelect-icon": {
                    color: fieldErrors.paymentType ? "red" : "#fff",
                  },
                }}
              >
                {["COD", "COP", "2 BD", "5 BD", "7 BD", "15 BD", "30 BD"].map(
                  (t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  )
                )}
              </Select>
              {fieldErrors.paymentType && (
                <FormHelperText sx={{ color: "red" }}>
                  {fieldErrors.paymentType}
                </FormHelperText>
              )}
            </FormControl>

            {/* Payment Method - CORRIGIDO (asterisco automático do MUI) */}
            <FormControl fullWidth error={!!fieldErrors.paymentMethod} required>
              <InputLabel
                id="payment-method-label"
                sx={{
                  color: fieldErrors.paymentMethod ? "red" : "#fff",
                  "&.Mui-focused": {
                    color: fieldErrors.paymentMethod ? "red" : "#fff",
                  },
                  // ✅ força o asterisco automático a ficar vermelho
                  "& .MuiFormLabel-asterisk": { color: "red" },
                  "& .MuiInputLabel-asterisk": { color: "red" },
                }}
              >
                Payment Method
              </InputLabel>
              <Select
                labelId="payment-method-label"
                value={formData.paymentMethod}
                onChange={(e) => {
                  handleFieldChange("paymentMethod", e.target.value);
                  // Limpar o erro quando o usuário selecionar algo
                  if (fieldErrors.paymentMethod) {
                    clearFieldError("paymentMethod");
                  }
                }}
                label="Payment Method"
                color="secondary"
                sx={{
                  "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                    borderColor: "red !important",
                  },
                  "& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
                    borderColor: "currentColor",
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: fieldErrors.paymentMethod ? "red" : "#fff",
                    },
                    "&:hover fieldset": {
                      borderColor: fieldErrors.paymentMethod ? "red" : "#fff",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: fieldErrors.paymentMethod ? "red" : "#fff",
                    },
                  },
                  "& .MuiSelect-icon": {
                    color: fieldErrors.paymentMethod ? "red" : "#fff",
                  },
                }}
              >
                {[
                  "ACH",
                  "Check",
                  "Zelle",
                  "Venmo",
                  "Cashapp",
                  "Uship",
                  "Cash",
                  "PayPal",
                  "Credit card",
                  "Debit",
                  "Comcheck",
                  "Echeck",
                  "Electronic Check",
                ].map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
              {fieldErrors.paymentMethod && (
                <FormHelperText sx={{ color: "red" }}>
                  {fieldErrors.paymentMethod}
                </FormHelperText>
              )}
            </FormControl>

            {/*
            <TextField
              label="Broker Fee"
              type="number"
              value={formData.brokerFee}
              onChange={(e) => {
                handleFieldChange("brokerFee", e.target.value);
                // Limpar o erro quando o usuário selecionar algo
                if (fieldErrors.brokerFee) {
                  clearFieldError("brokerFee");
                }
              }}
              color="secondary"
              error={!!fieldErrors.brokerFee}
              helperText={fieldErrors.brokerFee}
              sx={{
                "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "red !important",
                },
                "& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "currentColor",
                },
              }}
            />
            <TextField
              label="Storage Fee"
              type="number"
              value={formData.storageFee}
              onChange={(e) => {
                handleFieldChange("storageFee", e.target.value);
                // Limpar o erro quando o usuário selecionar algo
                if (fieldErrors.storageFee) {
                  clearFieldError("storageFee");
                }
              }}
              color="secondary"
              error={!!fieldErrors.storageFee}
              helperText={fieldErrors.storageFee}
              sx={{
                "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "red !important",
                },
                "& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "currentColor",
                },
              }}
            />
            */}
          </FormContainer>
        </GroupedContainer>

        {/* ---------------- Load Items Info ---------------- */}
        <GroupedContainer className="mt-[5rem]">
          <InfoSection title="Load Items Info" />
          {formData.cars.map((v, i) => (
            <React.Fragment key={i}>
              <FormContainer grid={4}>
                <TextField
                  label="VIN"
                  value={v.vin}
                  onChange={(e) => handleVehicleChange(i, "vin", e.target.value)}
                  color="secondary"
                />
                <TextField
                  label="Year"
                  type="number"
                  value={v.year}
                  onChange={(e) => handleVehicleChange(i, "year", e.target.value)}
                  required
                  color="secondary"
                  error={!!fieldErrors[`car${i}Year`]}
                  helperText={fieldErrors[`car${i}Year`]}
                  sx={{
                    "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                      borderColor: "red !important",
                    },
                    "& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
                      borderColor: "currentColor",
                    },
                  }}
                />
                <TextField
                  label="Make"
                  value={v.make}
                  onChange={(e) => handleVehicleChange(i, "make", e.target.value)}
                  required
                  color="secondary"
                  error={!!fieldErrors[`car${i}Make`]}
                  helperText={fieldErrors[`car${i}Make`]}
                  sx={{
                    "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                      borderColor: "red !important",
                    },
                    "& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
                      borderColor: "currentColor",
                    },
                  }}
                />
                <TextField
                  label="Model"
                  value={v.model}
                  onChange={(e) => handleVehicleChange(i, "model", e.target.value)}
                  required
                  color="secondary"
                  error={!!fieldErrors[`car${i}Model`]}
                  helperText={fieldErrors[`car${i}Model`]}
                  sx={{
                    "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                      borderColor: "red !important",
                    },
                    "& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
                      borderColor: "currentColor",
                    },
                  }}
                />
                <FormControl fullWidth error={!!fieldErrors[`car${i}CarType`]}>
                  <InputLabel id={`carType-label-${i}`}>Car Type</InputLabel>
                  <Select
                    labelId={`carType-label-${i}`}
                    value={v.carType}
                    onChange={(e) => handleVehicleChange(i, "carType", e.target.value)}
                    label="Car Type"
                    color="secondary"
                    required
                    sx={{
                      "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                        borderColor: "red !important",
                      },
                      "& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
                        borderColor: "currentColor",
                      },
                    }}
                  >
                    {carTypes.map((ct) => (
                      <MenuItem key={ct.id} value={ct.id}>
                        {ct.type}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldErrors[`car${i}CarType`] && (
                    <FormHelperText>{fieldErrors[`car${i}CarType`]}</FormHelperText>
                  )}
                </FormControl>
                <TextField
                  label="Lot Number"
                  value={v.lotNumber}
                  onChange={(e) => handleVehicleChange(i, "lotNumber", e.target.value)}
                  color="secondary"
                />
                <TextField
                  label="Buyer Number"
                  value={v.buyerNumber}
                  onChange={(e) => handleVehicleChange(i, "buyerNumber", e.target.value)}
                  color="secondary"
                />
                <FormControl fullWidth error={!!fieldErrors[`car${i}IsOperable`]}>
                  <InputLabel id={`is-operable-label-${i}`}>Is Operable</InputLabel>
                  <Select
                    labelId={`is-operable-label-${i}`}
                    value={v.isOperable}
                    onChange={(e) => handleVehicleChange(i, "isOperable", e.target.value)}
                    label="Is Operable"
                    color="secondary"
                    required
                    sx={{
                      "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                        borderColor: "red !important",
                      },
                      "& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
                        borderColor: "currentColor",
                      },
                    }}
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </Select>
                  {fieldErrors[`car${i}IsOperable`] && (
                    <FormHelperText>{fieldErrors[`car${i}IsOperable`]}</FormHelperText>
                  )}
                </FormControl>
              </FormContainer>
              {i !== 0 && (
                <div className="flex justify-center">
                  <IconButton onClick={() => removeVehicle(i)} color="error">
                    X
                  </IconButton>
                </div>
              )}
            </React.Fragment>
          ))}

          <SecondaryButton onClick={addVehicle} className="bg-amber-500 hover:bg-amber-600">
            <IoMdAddCircle className="mr-2" /> Add Vehicle
          </SecondaryButton>
        </GroupedContainer>

        {/* ---------------- Pickup Info ---------------- */}
        <GroupedContainer className="mt-[5rem]">
          <InfoSection title="Pickup Info" />
          <FormContainer grid={4}>
            <TextField
              label="Contact Company"
              value={formData.PickupBusinessName}
              onChange={(e) => handleFieldChange("PickupBusinessName", e.target.value)}
              color="secondary"
            />
            <FormControl fullWidth>
              <InputLabel id="pickup-location-type-label">Location Type</InputLabel>
              <Select
                labelId="pickup-location-type-label"
                value={formData.pickup_location_type}
                onChange={(e) => handleFieldChange("pickup_location_type", e.target.value)}
                label="Location Type"
                color="secondary"
              >
                {[
                  "Airport",
                  "Auction",
                  "Business",
                  "Dealership",
                  "Parking",
                  "Port",
                  "Repo",
                  "Residence",
                  "Terminal",
                ].map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Contact Name"
              value={formData.contactNamepickup}
              onChange={(e) => handleFieldChange("contactNamepickup", e.target.value)}
              color="secondary"
            />
            <TextField
              label="Email"
              value={formData.emailpickup}
              onChange={(e) => handleFieldChangeWithValidation("emailpickup", e.target.value)}
              color="secondary"
              error={!!fieldErrors.emailpickup}
              helperText={fieldErrors.emailpickup}
              sx={{
                "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "red !important",
                },
                "& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "currentColor",
                },
              }}
            />
            <TextField
              label="Phone"
              value={formData.phonepickup}
              onChange={(e) => handleFieldChangeWithValidation("phonepickup", e.target.value)}
              color="secondary"
              error={!!fieldErrors.phonepickup}
              helperText={fieldErrors.phonepickup}
              sx={{
                "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "red !important",
                },
                "& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "currentColor",
                },
              }}
            />
            {/* Campo de data CORRIGIDO - Pickup */}
            <TextField
              label="Estimated Date"
              type="date"
              InputLabelProps={{
                shrink: true,
                style: { color: fieldErrors.expected_pickup_date ? "red" : "#fff" },
              }}
              value={formData.expected_pickup_date}
              onChange={(e) =>
                handleFieldChangeWithValidation("expected_pickup_date", e.target.value)
              }
              required
              color="secondary"
              error={!!fieldErrors.expected_pickup_date}
              helperText={fieldErrors.expected_pickup_date}
              sx={{
                "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "red !important",
                },
                "& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "currentColor",
                },
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  filter: fieldErrors.expected_pickup_date
                    ? "invert(18%) sepia(94%) saturate(7481%) hue-rotate(357deg) brightness(97%) contrast(107%)"
                    : "invert(100%)",
                },
                '& input[type="date"]': {
                  color: fieldErrors.expected_pickup_date ? "red" : "#fff",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: fieldErrors.expected_pickup_date ? "red" : "#fff",
                  },
                  "&:hover fieldset": {
                    borderColor: fieldErrors.expected_pickup_date ? "red" : "#fff",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: fieldErrors.expected_pickup_date ? "red" : "#fff",
                  },
                  "& .MuiSvgIcon-root": {
                    color: fieldErrors.expected_pickup_date ? "red" : "#fff",
                  },
                },
                "& .MuiFormHelperText-root": {
                  color: "red",
                },
              }}
            />
          </FormContainer>
          <TextField
            label="Notes"
            multiline
            rows={4}
            fullWidth
            value={formData.pickup_notes}
            onChange={(e) => handleFieldChange("pickup_notes", e.target.value)}
            color="secondary"
          />

          <FormContainer grid={4}>
            <TextField
              label="Address"
              required
              value={formData.addresspickup}
              onChange={(e) =>
                handleFieldChangeWithValidation("addresspickup", e.target.value)
              }
              color="secondary"
              error={!!fieldErrors.addresspickup}
              helperText={fieldErrors.addresspickup}
              sx={{
                "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "red !important",
                },
                "& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "currentColor",
                },
              }}
            />
            <TextField
              label="City"
              required
              value={formData.citypickup}
              onChange={(e) => handleFieldChangeWithValidation("citypickup", e.target.value)}
              color="secondary"
              error={!!fieldErrors.citypickup}
              helperText={fieldErrors.citypickup}
              sx={{
                "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "red !important",
                },
                "& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "currentColor",
                },
              }}
            />
            <TextField
              label="State"
              required
              value={formData.Statepickup}
              onChange={(e) =>
                handleFieldChangeWithValidation("Statepickup", e.target.value)
              }
              color="secondary"
              error={!!fieldErrors.Statepickup}
              helperText={fieldErrors.Statepickup}
              sx={{
                "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "red !important",
                },
                "& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "currentColor",
                },
              }}
            />
            <TextField
              label="Zipcode"
              required
              value={formData.zipcodepickup}
              onChange={(e) => handleZipcodeChange("zipcodepickup", e.target.value)}
              color="secondary"
              error={!!fieldErrors.zipcodepickup}
              helperText={fieldErrors.zipcodepickup}
              sx={{
                "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "red !important",
                },
                "& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "currentColor",
                },
              }}
            />
          </FormContainer>
        </GroupedContainer>

        {/* ---------------- Delivery Info ---------------- */}
        <GroupedContainer className="mt-[5rem]">
          <InfoSection title="Delivery Info" />
          <FormContainer grid={4}>
            <TextField
              label="Contact Company"
              value={formData.DeliveryBusinessName}
              onChange={(e) => handleFieldChange("DeliveryBusinessName", e.target.value)}
              color="secondary"
            />
            <FormControl fullWidth>
              <InputLabel id="delivery-location-type-label">Location Type</InputLabel>
              <Select
                labelId="delivery-location-type-label"
                value={formData.delivery_location_type}
                onChange={(e) => handleFieldChange("delivery_location_type", e.target.value)}
                label="Location Type"
                color="secondary"
              >
                {[
                  "Airport",
                  "Auction",
                  "Business",
                  "Dealership",
                  "Parking",
                  "Port",
                  "Repo",
                  "Residence",
                  "Terminal",
                ].map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Contact Name"
              value={formData.contactNamedelivery}
              onChange={(e) => handleFieldChange("contactNamedelivery", e.target.value)}
              color="secondary"
            />
            <TextField
              label="Email"
              value={formData.emaildelivery}
              onChange={(e) => handleFieldChangeWithValidation("emaildelivery", e.target.value)}
              color="secondary"
              error={!!fieldErrors.emaildelivery}
              helperText={fieldErrors.emaildelivery}
              sx={{
                "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "red !important",
                },
                "& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "currentColor",
                },
              }}
            />
            <TextField
              label="Phone"
              value={formData.phonedelivery}
              onChange={(e) =>
                handleFieldChangeWithValidation("phonedelivery", e.target.value)
              }
              color="secondary"
              error={!!fieldErrors.phonedelivery}
              helperText={fieldErrors.phonedelivery}
              sx={{
                "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "red !important",
                },
                "& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "currentColor",
                },
              }}
            />
            {/* Campo de data CORRIGIDO - Delivery */}
            <TextField
              label="Estimated Date"
              type="date"
              InputLabelProps={{
                shrink: true,
                style: { color: fieldErrors.expected_dropoff_date ? "red" : "#fff" },
              }}
              value={formData.expected_dropoff_date}
              onChange={(e) =>
                handleFieldChangeWithValidation("expected_dropoff_date", e.target.value)
              }
              required
              color="secondary"
              error={!!fieldErrors.expected_dropoff_date}
              helperText={fieldErrors.expected_dropoff_date}
              sx={{
                "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "red !important",
                },
                "& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "currentColor",
                },
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  filter: fieldErrors.expected_dropoff_date
                    ? "invert(18%) sepia(94%) saturate(7481%) hue-rotate(357deg) brightness(97%) contrast(107%)"
                    : "invert(100%)",
                },
                '& input[type="date"]': {
                  color: fieldErrors.expected_dropoff_date ? "red" : "#fff",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: fieldErrors.expected_dropoff_date ? "red" : "#fff",
                  },
                  "&:hover fieldset": {
                    borderColor: fieldErrors.expected_dropoff_date ? "red" : "#fff",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: fieldErrors.expected_dropoff_date ? "red" : "#fff",
                  },
                  "& .MuiSvgIcon-root": {
                    color: fieldErrors.expected_dropoff_date ? "red" : "#fff",
                  },
                },
                "& .MuiFormHelperText-root": {
                  color: "red",
                },
              }}
            />
          </FormContainer>
          <TextField
            label="Notes"
            multiline
            rows={4}
            fullWidth
            value={formData.delivery_notes}
            onChange={(e) => handleFieldChange("delivery_notes", e.target.value)}
            color="secondary"
          />
          <FormContainer grid={4}>
            <TextField
              label="Address"
              value={formData.addressdelivery}
              onChange={(e) =>
                handleFieldChangeWithValidation("addressdelivery", e.target.value)
              }
              required
              color="secondary"
              error={!!fieldErrors.addressdelivery}
              helperText={fieldErrors.addressdelivery}
              sx={{
                "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "red !important",
                },
                "& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "currentColor",
                },
              }}
            />
            <TextField
              label="City"
              value={formData.citydelivery}
              onChange={(e) =>
                handleFieldChangeWithValidation("citydelivery", e.target.value)
              }
              required
              color="secondary"
              error={!!fieldErrors.citydelivery}
              helperText={fieldErrors.citydelivery}
              sx={{
                "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "red !important",
                },
                "& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "currentColor",
                },
              }}
            />
            <TextField
              label="State"
              value={formData.Statedelivery}
              onChange={(e) =>
                handleFieldChangeWithValidation("Statedelivery", e.target.value)
              }
              required
              color="secondary"
              error={!!fieldErrors.Statedelivery}
              helperText={fieldErrors.Statedelivery}
              sx={{
                "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "red !important",
                },
                "& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "currentColor",
                },
              }}
            />
            <TextField
              label="Zipcode"
              value={formData.zipcodedelivery}
              onChange={(e) =>
                handleZipcodeChange("zipcodedelivery", e.target.value)
              }
              required
              color="secondary"
              error={!!fieldErrors.zipcodedelivery}
              helperText={fieldErrors.zipcodedelivery}
              sx={{
                "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "red !important",
                },
                "& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "currentColor",
                },
              }}
            />
          </FormContainer>
        </GroupedContainer>

        {/* ---------------- Submit / Reset ---------------- */}
        <div className="flex justify-end mb-10 mt-10">
          <SecondaryButton
            onClick={handleSubmit}
            className="bg-amber-500 hover:bg-amber-600"
            disabled={submitLoading}
          >
            {submitLoading ? (
              <div className="flex items-center justify-center gap-2">
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  ></path>
                </svg>
                Loading...
              </div>
            ) : (
              <>
                <RiSendPlaneFill className="mr-2" /> Submit
              </>
            )}
          </SecondaryButton>
          <SecondaryButton
            onClick={clearAll}
            className="bg-red-500 hover:bg-red-600 ml-4"
            disabled={submitLoading}
          >
            <GrPowerReset className="mr-2" /> Reset
          </SecondaryButton>
        </div>

        {/* Snackbar para exibir mensagens de feedback */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={5000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{
              width: "500px",
              backgroundColor:
                snackbarSeverity === "error"
                  ? "#dc2626"
                  : snackbarSeverity === "success"
                  ? "#059669"
                  : snackbarSeverity === "warning"
                  ? "#d97706"
                  : "#dc2626",
              color: "#fff",
              "& .MuiAlert-icon": {
                color: "#fff",
              },
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </div>
    </AuthenticatedLayout>
  );
}

export default CreateLoad;
